/**
 * Patient Access (PAC) intro call — the rep-staffed 15-minute call that precedes
 * the provider consultation.
 *
 * Constants, plus the appointment-type predicate that decides whether a given
 * appointment offers the call card. PAC is otherwise a consumer of the generic
 * slot-hold machinery in `slot-holds.ts`; it has no bespoke code path.
 *
 * NOTE: `ci-intro-call` is deliberately distinct from the pre-existing
 * `intro-call` code, which is the care-team call mapped to Healthie's
 * `phone_call_pn`. They are unrelated.
 */

import { Appointment } from '@medplum/fhirtypes';
import {
  APPOINTMENT_TYPES_SYSTEM,
  CONSULTATION_APPOINTMENT_CODES,
  IvrTemplateId,
  PROCEDURE_APPOINTMENT_CODES,
  PN_CONSULTATION_CODE,
} from './constants';

export const PAC_INTRO_CALL_CODE = 'ci-intro-call';
export const PAC_INTRO_CALL_DISPLAY = 'Your Everself Care Intake call';
export const PAC_INTRO_CALL_DURATION_MINUTES = 15;

/**
 * The appointment's type lives on `appointmentType`, not `serviceType`.
 *
 * `AppointmentBuilder` reads the ActivityDefinition code once and sends it to
 * two different places: `Appointment.appointmentType` and `Slot.serviceType`.
 * Nothing ever writes `Appointment.serviceType` — reading it here made this
 * predicate return false for every appointment in every environment.
 */
export function isCiIntroCallAppointment(appointment: Pick<Appointment, 'appointmentType'>): boolean {
  return Boolean(appointment.appointmentType?.coding?.some((coding) => coding.code === PAC_INTRO_CALL_CODE));
}

/**
 * The PN consultation — under Intake 1.1, every consult self-scheduled from the
 * eligibility flow. It carries the same card as the PAC intro call.
 *
 * Narrower than `isConsultationAppointment` on purpose: that one also matches the
 * NP consult, which keeps the generic chart-note link rather than a call card.
 *
 * Scoped to our own code system for the reason given below, and reads
 * `appointmentType` rather than `serviceType` for the reason given above.
 */
export function isPnConsultationAppointment(appointment: Pick<Appointment, 'appointmentType'>): boolean {
  return Boolean(
    appointment.appointmentType?.coding?.some(
      (coding) => coding.system === APPOINTMENT_TYPES_SYSTEM && coding.code === PN_CONSULTATION_CODE
    )
  );
}

/**
 * The provider consultation — the appointment the warm hand-off call follows.
 *
 * The PAC joins its last minutes rather than booking a call of their own, so this
 * is what the hand-off card attaches to. Scoped to our own code system, unlike
 * the intro-call predicate above: `app-initial-consultation-visit` is a generic
 * enough string that a foreign coding must not hand a rep a chart card on
 * somebody else's appointment.
 *
 * Reads `appointmentType`, never `serviceType` — see the note above.
 */
export function isConsultationAppointment(appointment: Appointment): boolean {
  return Boolean(
    appointment.appointmentType?.coding?.some(
      (coding) =>
        coding.system === APPOINTMENT_TYPES_SYSTEM &&
        coding.code !== undefined &&
        (CONSULTATION_APPOINTMENT_CODES as readonly string[]).includes(coding.code)
    )
  );
}

/**
 * Index (stage-advancing) procedure — the same system-scoped rule as
 * `isConsultationAppointment`, over `PROCEDURE_APPOINTMENT_CODES`. Removals are
 * follow-ups and are not in that list.
 */
export function isProcedureAppointment(appointment: Appointment): boolean {
  return Boolean(
    appointment.appointmentType?.coding?.some(
      (coding) =>
        coding.system === APPOINTMENT_TYPES_SYSTEM &&
        coding.code !== undefined &&
        (PROCEDURE_APPOINTMENT_CODES as readonly string[]).includes(coding.code)
    )
  );
}

/** One branch of the phone tree: a digit on a given menu template. */
export interface PacIvrBranch {
  templateId: string;
  digit: number;
}

/**
 * The phone-tree branches that reach Patient Access.
 *
 * "Press 2" is the only thing that distinguishes an intake call. Nothing else
 * does: every inbound number funnels into the same menu, no routing action
 * targets the PAC role, and the intake hours set carries no Location.
 *
 * English only for now — the Spanish menu maps its digits independently and has
 * not been confirmed, so its presses are left out rather than guessed at. Add
 * the Spanish branch here once someone checks it.
 *
 * This has to track the live IVR configuration. Reordering the menu without
 * changing this silently re-points the whole report at another team.
 */
export const PAC_IVR_BRANCHES: PacIvrBranch[] = [{ templateId: IvrTemplateId.BUSINESS_HOURS_EN, digit: 2 }];
